import { Component, Input, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/angular';
import { ChatFlowService, LlmInfo } from '../services/chat.flow.service';
import { HttpStatusError } from '../services/chat.sse-parser';
import { MessageListComponent } from '../message-list/message-list.component';
import { TopicListComponent } from '../topic-list/topic-list.component';
import { UiService } from '../../../core/services/core.ui.service';

@Component({
    selector: 'flow-chat',
    template: require('./chat.component.html'),
    styles: [`
        .flow-sidebar {
            flex: 0 0 180px;
            border-right: 1px solid #e5e5e5;
            overflow-y: auto;
            padding-top: 12px;
        }
        .flow-sidebar-backdrop { display: none; }
        @media (max-width: 767px) {
            .flow-sidebar {
                position: fixed;
                top: 0; left: 0; bottom: 0;
                width: 260px;
                background: #fff;
                z-index: 1050;
                padding: 16px 12px;
                transform: translateX(-100%);
                transition: transform 0.25s ease;
            }
            .flow-sidebar.open {
                transform: translateX(0);
                box-shadow: 2px 0 8px rgba(0,0,0,0.18);
            }
            .flow-sidebar-backdrop {
                display: none;
                position: fixed;
                top: 0; left: 0; right: 0; bottom: 0;
                background: rgba(0,0,0,0.4);
                z-index: 1040;
            }
            .flow-sidebar-backdrop.open { display: block; }
        }
    `]
})
export class ChatComponent implements OnInit, OnDestroy {

    @Input() project: any;
    @Input() projectMenu: any;
    @Input() aiSettings: any;

    @ViewChild(MessageListComponent, { static: false }) messageList!: MessageListComponent;
    @ViewChild(TopicListComponent, { static: false }) topicList!: TopicListComponent;

    selectedTopicId: string | null = null;
    sidebarOpen: boolean = false;
    flowEnabled: boolean = false;
    flowDisabledReason: string = '';

    composerText: string = '';
    isSending: boolean = false;
    sendError: string | null = null;

    uiLocked: boolean = false;
    lockAlertType: 'danger' | 'warning' = 'danger';
    lockError: string | null = null;

    llmInfo: LlmInfo | null = null;
    llmInfoOpen: boolean = false;

    private sseSubscription: Subscription | null = null;

    constructor(
        private chatFlowService: ChatFlowService,
        private uiService: UiService,
        private cdr: ChangeDetectorRef,
        private stateService: StateService,
        private ngZone: NgZone
    ) {}

    ngOnInit() {
        if (!this.aiSettings) {
            this.flowDisabledReason = 'AI Flow is not configured for this project.';
        } else if (this.aiSettings.ai_blocked_by !== null) {
            this.flowDisabledReason = `AI Flow is disabled by ${this.aiSettings.ai_blocked_by}.`;
        } else if (!this.aiSettings.ai_enabled || this.aiSettings.ai_enabled.value !== true) {
            this.flowDisabledReason = 'AI Flow is not enabled for this project.';
        } else if (!this.aiSettings.llm_provider || !this.aiSettings.llm_provider.value) {
            this.flowDisabledReason = 'No AI model is configured for this project.';
        } else if (!this.aiSettings.llm_key || !this.aiSettings.llm_key.value) {
            this.flowDisabledReason = 'No AI API key is configured for this project.';
        } else {
            this.flowEnabled = true;
            this.loadLlmInfo();
            const topicId = this.stateService.params['topicId'];
            if (topicId) {
                this.selectedTopicId = topicId;
            }
        }
    }

    private loadLlmInfo() {
        this.chatFlowService.getLlmInfo(this.project.unixName)
            .then(info => { this.llmInfo = info; });
    }

    toggleSidebar() { this.sidebarOpen = !this.sidebarOpen; }
    closeSidebar()  { this.sidebarOpen = false; }

    onTopicSelected(topicId: string | null) {
        this.closeSidebar();
        this.stateService.go('.', { topicId: topicId || null }, { reload: false, notify: true })
            .then(() => { this.selectedTopicId = topicId; });
    }

    onNewChat() {
        this.closeSidebar();
        this.stateService.go('.', { topicId: null }, { reload: false, notify: true })
            .then(() => { this.selectedTopicId = null; });
    }

    async sendMessage() {
        const text = this.composerText.trim();
        if (!text || this.isSending || this.uiLocked) { return; }

        this.composerText = '';
        this.isSending = true;
        this.sendError = null;

        let topicId = this.selectedTopicId;

        if (!topicId) {
            try {
                const topic = await this.chatFlowService.createTopic(this.project.unixName, text);
                this.topicList.addTopic(topic);
                this.messageList.skipLoad();
                this.selectedTopicId = topic.id;
                topicId = topic.id;
                this.stateService.go('.', { topicId: topic.id }, { reload: false, notify: true });
                this.cdr.detectChanges();
            } catch {
                this.isSending = false;
                this.sendError = 'Could not start the conversation. The AI service may be unavailable — please try again.';
                return;
            }
        }

        this.messageList.addUserMessage(text);
        this.messageList.addStreamingMessage();

        this.sseSubscription = this.chatFlowService.streamChat(this.project.unixName, topicId, text)
            .subscribe({
                next: (event) => {
                    // fetchEventSource reads via ReadableStream.getReader().read(), which
                    // Zone.js 0.8.x does not fully patch, so callbacks arrive outside NgZone.
                    // Wrap every handler to ensure change detection fires on each event.
                    this.ngZone.run(() => {
                    if (event.type === 'token') {
                        this.messageList.appendToken(event.delta);
                    } else if (event.type === 'done') {
                        this.messageList.finalizeMessage(event.messageId, false, event.citations);
                        this.isSending = false;
                        this.sseSubscription = null;
                    } else if (event.type === 'error') {
                        this.messageList.finalizeMessage(-1, true);
                        this.sendError = 'The AI response failed. Please try sending again.';
                        this.isSending = false;
                        this.sseSubscription = null;
                    } else if (event.type === 'tool_start') {
                        this.messageList.startToolCall(event.toolName, event.label);
                    } else if (event.type === 'tool_end') {
                        this.messageList.endToolCall(event.toolName);
                    }
                    });
                },
                error: (err) => {
                    this.ngZone.run(() => {
                    this.messageList.finalizeMessage(-1, true);
                    this.isSending = false;
                    this.sseSubscription = null;
                    this.handleStreamError(err);
                    });
                }
            });
    }

    private handleStreamError(err: any) {
        if (err instanceof HttpStatusError) {
            switch (err.status) {
                case 400:
                    this.uiLocked = true;
                    this.lockAlertType = 'danger';
                    this.lockError = 'The AI provider, model name, or API key is configured incorrectly. Contact your project or site administrator.';
                    break;
                case 403:
                    this.uiLocked = true;
                    this.lockAlertType = 'warning';
                    this.lockError = 'You are not allowed to use AI Flow chat.';
                    break;
                case 500:
                    this.uiLocked = true;
                    this.lockAlertType = 'danger';
                    this.lockError = 'The AI provider\'s service is not available. Please contact your administrator.';
                    break;
                case 503:
                    this.uiService.message.showWarning('The AI provider\'s service is too busy. Please try again later.', 'AI Service Busy');
                    break;
                case 429:
                    this.uiService.message.showWarning('Your quota with the AI provider has been reached.', 'Quota Reached');
                    break;
                default:
                    this.sendError = 'Connection to the AI service was lost. Please try again.';
            }
        } else {
            this.sendError = 'Connection to the AI service was lost. Please try again.';
        }
    }

    stopStreaming() {
        if (this.sseSubscription) {
            this.sseSubscription.unsubscribe();
            this.sseSubscription = null;
        }
        this.messageList.finalizeMessage(-1);
        this.isSending = false;
    }

    ngOnDestroy(): void {
        if (this.sseSubscription) {
            this.sseSubscription.unsubscribe();
            this.sseSubscription = null;
        }
    }

    onComposeKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
        if (event.key === 'Escape') {
            this.llmInfoOpen = false;
        }
    }

    toggleLlmInfo() {
        this.llmInfoOpen = !this.llmInfoOpen;
    }

    get selectedTopicTitle(): string | null {
        if (!this.selectedTopicId || !this.topicList) { return null; }
        const topic = this.topicList.topics.find(t => t.id === this.selectedTopicId);
        return topic ? topic.title : null;
    }
}
